<template>
  <div>
    <a-card v-if="canManageProviders" title="题库/AI 配置">
      <template #extra><a-button type="primary" @click="openModal()">新增配置</a-button></template>
      <a-table :data-source="list" :columns="columns" rowKey="id" :loading="loading">
        <template #enabled="{ record }">
          <a-tag :color="record.enabled ? 'green' : 'red'">{{ record.enabled ? '启用' : '禁用' }}</a-tag>
        </template>
        <template #quota="{ record }">
          <span>{{ record.quota === 0 ? '用尽/禁用' : record.quota === -1 ? '无限' : record.quota }}</span>
        </template>
        <template #config="{ record }">
          <span class="ellipsis">{{ compactJSON(record.config) }}</span>
        </template>
        <template #operation="{ record }">
          <a-space>
            <a-button size="small" @click="openModal(record)">编辑</a-button>
            <a-popconfirm title="确认删除该配置？" ok-text="确定" cancel-text="取消" @confirm="remove(record.id)">
              <a style="color: red;">删除</a>
            </a-popconfirm>
          </a-space>
        </template>
      </a-table>
    </a-card>

    <a-card v-if="canManagePricing" class="mt" title="模型价目表">
      <template #extra><a-button @click="openPricingModal()">新增模型</a-button></template>
      <a-table :data-source="pricing" :columns="priceColumns" rowKey="id" :loading="priceLoading">
        <template #enabled="{ record }">
          <a-tag :color="record.enabled ? 'green' : 'red'">{{ record.enabled ? '启用' : '禁用' }}</a-tag>
        </template>
        <template #operation="{ record }">
          <a-space>
            <a-button size="small" @click="openPricingModal(record)">编辑</a-button>
            <a-popconfirm title="确认删除该模型？" ok-text="确定" cancel-text="取消" @confirm="removePricing(record.id)">
              <a style="color: red;">删除</a>
            </a-popconfirm>
          </a-space>
        </template>
      </a-table>
    </a-card>

    <a-modal v-model:visible="modalVisible" :title="isEdit ? '编辑配置' : '新增配置'" @ok="handleSubmit" @cancel="resetForm">
      <a-form :model="form" layout="vertical">
        <a-form-item label="类型">
          <a-select v-model:value="form.kind">
            <a-select-option value="search">题库（search）</a-select-option>
            <a-select-option value="ai">AI（ai）</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="提供方">
          <a-select v-model:value="form.provider" :options="form.kind === 'search' ? searchProviders : aiProviders" />
        </a-form-item>
        <a-form-item label="名称">
          <a-input v-model:value="form.name" placeholder="自定义名称" />
        </a-form-item>
        <a-form-item label="排序">
          <a-input-number v-model:value="form.sort" :precision="0" style="width: 100%;" />
        </a-form-item>
        <a-form-item v-if="form.kind === 'ai'" label="选择模型价目（可选）">
          <a-select v-model:value="form.pricingId" allowClear :options="pricingOptions" />
        </a-form-item>
        <template v-if="form.kind === 'search'">
          <a-form-item v-if="needsToken(form.provider)" label="Token">
            <a-input v-model:value="form.search.token" placeholder="请输入 token" />
          </a-form-item>
          <a-form-item v-if="form.provider === 'aidian'" label="YToken">
            <a-input v-model:value="form.search.yToken" placeholder="请输入 yToken" />
          </a-form-item>
          <a-form-item v-if="form.provider === 'custom'" label="自定义接口">
            <a-input v-model:value="form.search.url" placeholder="URL" class="mb8" />
            <a-select v-model:value="form.search.method" :options="httpMethods" class="mb8" />
            <a-input v-model:value="form.search.body" placeholder="Body" class="mb8" />
            <a-input v-model:value="form.search.answer" placeholder="答案路径，如 result.answers.0.0" />
          </a-form-item>
        </template>
        <template v-else>
          <a-form-item label="API Key">
            <a-input v-model:value="form.ai.apiKey" />
          </a-form-item>
          <a-form-item label="提示词（可选）">
            <a-textarea v-model:value="form.ai.prompt" :rows="3" />
          </a-form-item>
        </template>
        <a-form-item label="额度（-1 = 无限，0 = 用尽禁用）">
          <a-input-number v-model:value="form.quota" :min="-1" :step="0.01" style="width: 100%;" />
        </a-form-item>
        <a-form-item label="启用">
          <a-switch v-model:checked="form.enabled" />
        </a-form-item>
      </a-form>
    </a-modal>

    <a-modal v-model:visible="pricingModal" :title="priceEdit ? '编辑模型' : '新增模型'" @ok="savePricing">
      <a-form :model="priceForm" layout="vertical">
        <a-form-item label="提供方">
          <a-select v-model:value="priceForm.provider" :options="aiProviderOptions" allowClear />
        </a-form-item>
        <a-form-item label="模型">
          <a-input v-model:value="priceForm.model" />
        </a-form-item>
        <a-form-item label="API URL">
          <a-input v-model:value="priceForm.apiUrl" placeholder="https://api.../v1/chat/completions" />
        </a-form-item>
        <a-form-item label="每 1k 输入单价">
          <a-input-number v-model:value="priceForm.inputPrice" :min="0" :step="0.0001" style="width: 100%;" />
        </a-form-item>
        <a-form-item label="每 1k 输出单价">
          <a-input-number v-model:value="priceForm.outputPrice" :min="0" :step="0.0001" style="width: 100%;" />
        </a-form-item>
        <a-form-item label="货币">
          <a-input v-model:value="priceForm.currency" placeholder="默认 CNY" />
        </a-form-item>
        <a-form-item label="启用">
          <a-switch v-model:checked="priceForm.enabled" />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import { defineComponent, reactive, ref, onMounted, computed } from 'vue'
import {
  getProviders, createProvider, updateProvider, deleteProvider,
  getPricing, createPricing, updatePricing, deletePricing
} from '@/api/TikuComponentApi'
import { message } from 'ant-design-vue'
import { hasPermission, PERMISSIONS } from '@/utils/permissions'
import { asArrayResult } from '@/utils/result'

export default defineComponent({
  name: 'ProviderManage',
  setup() {
    const list = ref([])
    const pricing = ref([])
    const loading = ref(false)
    const priceLoading = ref(false)
    const modalVisible = ref(false)
    const pricingModal = ref(false)
    const isEdit = ref(false)
    const editId = ref(null)
    const priceEdit = ref(false)
    const priceEditId = ref(null)
    const canManageProviders = hasPermission(PERMISSIONS.PROVIDER_MANAGE)
    const canManagePricing = hasPermission(PERMISSIONS.PRICING_MANAGE)
    const form = reactive({
      kind: 'search',
      provider: 'buguake',
      name: '',
      sort: 0,
      pricingId: undefined,
      search: { token: '', yToken: '', url: '', method: 'GET', body: '', answer: '' },
      ai: { apiKey: '', prompt: '' },
      quota: -1,
      enabled: true,
    })
    const priceForm = reactive({
      id: null,
      provider: 'deepseek',
      model: '',
      apiUrl: '',
      inputPrice: 0.0001,
      outputPrice: 0.0001,
      currency: 'CNY',
      enabled: true,
    })

    const searchProviders = [
      { label: '不挂科题库（免费）', value: 'buguake' },
      { label: 'icodef 题库（免费）', value: 'icodef' },
      { label: '万能题库（付费）', value: 'wanneng' },
      { label: '题库海题库（付费）', value: 'tikuhai' },
      { label: '爱点题库（付费）', value: 'aidian' },
      { label: 'enncy 言溪题库（付费）', value: 'enncy' },
      { label: '柠檬题库（付费）', value: 'lemon' },
      { label: '自定义(custom)', value: 'custom' },
    ]
    const aiProviders = [
      { label: 'DeepSeek', value: 'deepseek' },
      { label: 'OpenAI(GPT)', value: 'openai' },
      { label: 'Gemini', value: 'gemini' },
      { label: '千问(QWEN)', value: 'qwen' },
    ]
    const aiProviderOptions = [
      { label: 'DeepSeek', value: 'deepseek' },
      { label: 'OpenAI(GPT)', value: 'openai' },
      { label: 'Gemini', value: 'gemini' },
      { label: '千问(QWEN)', value: 'qwen' },
    ]
    const httpMethods = [
      { label: 'GET', value: 'GET' },
      { label: 'POST', value: 'POST' },
    ]
    const columns = [
      { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
      { title: '类型', dataIndex: 'kind', key: 'kind', width: 80 },
      { title: '提供方', dataIndex: 'provider', key: 'provider', width: 120 },
      { title: '名称', dataIndex: 'name', key: 'name', width: 140 },
      { title: '排序', dataIndex: 'sort', key: 'sort', width: 90 },
      { title: '额度', key: 'quota', slots: { customRender: 'quota' }, width: 120 },
      { title: '启用', key: 'enabled', slots: { customRender: 'enabled' }, width: 90 },
      { title: '配置', key: 'config', slots: { customRender: 'config' } },
      { title: '操作', key: 'operation', slots: { customRender: 'operation' }, width: 160 },
    ]
    const priceColumns = [
      { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
      { title: '提供方', dataIndex: 'provider', key: 'provider' },
      { title: '模型', dataIndex: 'model', key: 'model' },
      { title: 'API URL', dataIndex: 'apiUrl', key: 'apiUrl' },
      { title: '输入价/1k', dataIndex: 'inputPrice', key: 'inputPrice' },
      { title: '输出价/1k', dataIndex: 'outputPrice', key: 'outputPrice' },
      { title: '货币', dataIndex: 'currency', key: 'currency' },
      { title: '启用', key: 'enabled', slots: { customRender: 'enabled' }, width: 90 },
      { title: '操作', key: 'operation', slots: { customRender: 'operation' }, width: 160 },
    ]

    const loadProviders = () => {
      loading.value = true
      getProviders().then(res => {
        if (res?.status === 200) {
          list.value = asArrayResult(res.data)
        } else {
          list.value = []
          message.error(res?.message || '获取失败')
        }
      }).finally(() => loading.value = false)
    }
    const loadPricing = () => {
      priceLoading.value = true
      getPricing().then(res => {
        if (res?.status === 200) {
          pricing.value = asArrayResult(res.data)
        } else {
          pricing.value = []
          message.error(res?.message || '获取价目表失败')
        }
      }).finally(() => priceLoading.value = false)
    }
    const openModal = (record) => {
      if (record) {
        isEdit.value = true
        editId.value = record.id
        form.kind = record.kind
        form.provider = record.provider
        form.name = record.name
        form.sort = record.sort ?? 0
        form.quota = record.quota
        form.enabled = record.enabled
        form.pricingId = record.pricingId
        try {
          const cfg = typeof record.config === 'object' ? record.config : JSON.parse(record.config || '{}')
          if (record.kind === 'search') {
            form.search = { ...form.search, ...cfg }
          } else {
            form.ai = { ...form.ai, ...cfg }
          }
        } catch (e) {
          // ignore parse error
        }
      } else {
        resetForm()
      }
      modalVisible.value = true
    }

    const handleSubmit = () => {
      const payload = {
        kind: form.kind,
        provider: form.provider,
        name: form.name,
        sort: Number(form.sort ?? 0),
        quota: Number(form.quota ?? 0),
        enabled: form.enabled,
      }
      if (form.kind === 'search') {
        payload.config = { ...form.search }
      } else {
        payload.config = { ...form.ai }
        if (form.pricingId) {
          payload.pricingId = form.pricingId
        }
      }
      if (payload.quota == 0) payload.enabled = false
      const action = isEdit.value ? updateProvider(editId.value, payload) : createProvider(payload)
      action.then(res => {
        if (res?.status === 200) {
          message.success(isEdit.value ? '更新成功' : '创建成功')
          modalVisible.value = false
          loadProviders()
        } else {
          message.error(res?.message || '保存失败')
        }
      })
    }

    const remove = (id) => {
      deleteProvider(id).then(res => {
        if (res?.status === 200) {
          message.success('删除成功')
          loadProviders()
        } else {
          message.error(res?.message || '删除失败')
        }
      })
    }

    const resetPriceForm = () => {
      priceEdit.value = false
      priceEditId.value = null
      Object.assign(priceForm, { id: null, provider: 'deepseek', model: '', apiUrl: '', inputPrice: 0.0001, outputPrice: 0.0001, currency: 'CNY', enabled: true })
    }

    const openPricingModal = (record) => {
      if (record) {
        priceEdit.value = true
        priceEditId.value = record.id
        Object.assign(priceForm, { ...record, id: record.id })
      } else {
        resetPriceForm()
      }
      pricingModal.value = true
    }
    const savePricing = () => {
      const provider = priceForm.provider
      if (!provider) {
        message.error('请填写提供方')
        return
      }
      const payload = {
        provider,
        model: priceForm.model,
        apiUrl: priceForm.apiUrl,
        inputPrice: Number(priceForm.inputPrice || 0),
        outputPrice: Number(priceForm.outputPrice || 0),
        currency: priceForm.currency,
        enabled: priceForm.enabled,
      }
      const targetId = priceEditId.value || priceForm.id
      const action = targetId ? updatePricing(targetId, payload) : createPricing(payload)
      action.then(res => {
        if (res?.status === 200) {
          message.success('保存成功')
          pricingModal.value = false
          resetPriceForm()
          loadPricing()
        } else {
          message.error(res?.message || '保存失败')
        }
      })
    }
    const removePricing = (id) => {
      deletePricing(id).then(res => {
        if (res?.status === 200) {
          message.success('删除成功')
          loadPricing()
        }
      })
    }

    const compactJSON = (obj) => {
      try {
        return JSON.stringify(obj)
      } catch {
        return ''
      }
    }
    const needsToken = (provider) => ['buguake', 'icodef', 'wanneng', 'tikuhai', 'enncy', 'lemon'].includes(provider)

    const resetForm = () => {
      isEdit.value = false
      editId.value = null
      form.kind = 'search'
      form.provider = 'buguake'
      form.name = ''
      form.sort = 0
      form.pricingId = undefined
      form.search = { token: '', yToken: '', url: '', method: 'GET', body: '', answer: '' }
      form.ai = { apiKey: '', prompt: '' }
      form.quota = -1
      form.enabled = true
    }

    onMounted(() => {
      if (!canManageProviders && !canManagePricing) {
        message.error('无权限访问')
        return
      }
      if (canManageProviders) loadProviders()
      if (canManagePricing) loadPricing()
    })

    const pricingOptions = computed(() => (pricing.value || []).filter(i => i.enabled).map(i => ({
      label: `${i.provider}/${i.model} (${i.inputPrice}/${i.outputPrice})`,
      value: i.id,
    })))

    return {
      list, columns, loading,
      searchProviders, aiProviders, httpMethods,
      modalVisible, form, isEdit,
      pricing, priceColumns, pricingModal, priceForm, priceEdit,
      aiProviderOptions,
      pricingOptions,
      canManageProviders,
      canManagePricing,
      handleSubmit, openModal, remove,
      openPricingModal, savePricing, removePricing,
      compactJSON, needsToken,
      resetPriceForm
    }
  }
})
</script>

<style scoped>
.mt {
  margin-top: 16px;
}
.ellipsis {
  display: inline-block;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.mb8 {
  margin-bottom: 8px;
}
</style>
